import { useState } from 'react'
import AppBar from '../components/AppBar'
import routes from './routes'
import { MenuItem, Typography, Box, colors } from '@mui/material'
import { Navigate, useNavigate } from 'react-router-dom'
import Wrapper from '../components/Wrapper'
import useIdea from '../utils/hooks/useIdea'
import DialogAlert from '../components/DialogAlert'
import MoreOptionsMenu from '../components/MoreOptionsMenu'
import IdeaFieldForm, {
  EditableFields,
  FormValues
} from '../components/EditIdeaField'
import EditNameField from '../components/EditNameField'
import useSmallScreen from '../utils/hooks/useSmallScreen'
import db from '../db'
import { serverTimestamp } from 'firebase/firestore'
import { useSetRecoilState } from 'recoil'
import { ideasState } from '../state'

const Idea = () => {
  const navigate = useNavigate()
  const { data: idea, loading } = useIdea()
  const setIdeas = useSetRecoilState(ideasState)
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [openMenu, setOpenMenu] = useState<boolean>(false)
  const [editingField, setEditingField] = useState<EditableFields | void>()
  const isSmallScreen = useSmallScreen()

  if (loading) {
    return null
  }

  if (!idea) {
    return <Navigate to={routes.ideas} />
  }

  const handleSubmit = async (formValues: FormValues) => {
    if (idea) {
      await db.updateItem('ideas', idea.id, {
        lastUpdatedAt: serverTimestamp(),
        ...formValues
      })
      const updatedIdea = await db.getById('ideas', idea.id)
      if (updatedIdea) {
        setIdeas(ideas => ({
          ...ideas,
          data: ideas.data?.map(i =>
            i.id === updatedIdea.id ? { ...i, ...updatedIdea } : i
          )
        }))
      }
      setEditingField()
    }
  }

  return (
    <>
      <AppBar
        backTo={routes.ideas}
        actions={
          <MoreOptionsMenu
            open={openMenu}
            onOpen={() => setOpenMenu(true)}
            onClose={() => setOpenMenu(false)}
          >
            <MenuItem onClick={() => setOpenDeleteDialog(true)}>
              Delete
            </MenuItem>
          </MoreOptionsMenu>
        }
      />

      {idea && (
        <Wrapper marginTop={0}>
          <Box mb={4} mt={3}>
            <Typography
              color='primary'
              align={isSmallScreen ? 'left' : 'right'}
              variant='body2'
            >
              Edit your answers by clicking on them
            </Typography>
          </Box>
          <EditNameField
            name={idea.name}
            onSubmit={handleSubmit}
            onToggleEdit={setEditingField}
            isEditing={editingField === 'name'}
            isDisabled={!!editingField && editingField !== 'name'}
          />

          <IdeaFieldForm
            fieldName='problemDescription'
            defaultValue={idea.problemDescription}
            label='Describe the problem'
            isEditing={editingField === 'problemDescription'}
            onToggleEdit={setEditingField}
            onSubmit={handleSubmit}
            isDisabled={!!editingField && editingField !== 'problemDescription'}
          />

          {!!idea.problemDescription && (
            <IdeaFieldForm
              fieldName='solutionDescription'
              defaultValue={idea.solutionDescription}
              label='How would you like to solve this problem?'
              isEditing={editingField === 'solutionDescription'}
              onToggleEdit={setEditingField}
              onSubmit={handleSubmit}
              isDisabled={
                !!editingField && editingField !== 'solutionDescription'
              }
            />
          )}

          {!!idea.solutionDescription && (
            <IdeaFieldForm
              fieldName='marketDescription'
              defaultValue={idea.marketDescription}
              label='How will the market engage with your product?'
              isEditing={editingField === 'marketDescription'}
              onToggleEdit={setEditingField}
              onSubmit={handleSubmit}
              isDisabled={
                !!editingField && editingField !== 'marketDescription'
              }
            />
          )}

          {idea.marketDescription && (
            <>
              <Box
                bgcolor={colors.grey[200]}
                paddingX={2}
                pt={1.5}
                pb={2}
                marginX={-2}
              >
                <Typography variant='h6' component='h2' gutterBottom>
                  Validate Idea
                </Typography>
                <Typography paragraph>
                  The easy part is done. Now comes the fun. So far, you've made
                  quite a few assumptions: that you understand the problem, that
                  you know how to solve it and how the market will engage or
                  find your product.
                </Typography>
                <Typography>
                  The next step is to try to validate these assumptions. You can
                  acheive this by using a technique called{' '}
                  <Box component='span' fontWeight={500}>
                    The Painted Door Test
                  </Box>
                  , It consists of building a landing page (or a marketing
                  website) where you present your idea to the target audience,
                  you then allow them to sign up with their email for a beta
                  launch. When people sign up this way, you can figure out how
                  much initial interest there is in your idea.
                </Typography>
              </Box>
            </>
          )}
        </Wrapper>
      )}

      <DialogAlert
        open={openDeleteDialog}
        text='Are you sure you want to delete this idea?'
        confirmLabel='Delete Idea'
        onConfirm={async () => {
          if (idea) {
            await db.deleteItem('ideas', idea.id)
            setIdeas(currentIdeas => ({
              ...currentIdeas,
              data: (currentIdeas.data || []).filter(i => i.id !== idea.id)
            }))
            navigate(routes.ideas)
          }
        }}
        onClose={() => setOpenDeleteDialog(false)}
      />
    </>
  )
}

export default Idea
